import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useCallback, useMemo } from 'react';
import { workflowStatusColumn } from 'src/app/workflows/workflowStatusColumn';
import {
  changeAmountColumns,
  changeAmountExportRow,
} from 'src/app/changes/changeAmountColumns';
import { useNavigate } from 'react-router-dom';
import { ChangeOverviewDto } from 'src/api/queries/changes/useChangesOverview';
import { ViewActionIcon } from 'src/design/atoms/ViewActionIcon';
import { useTableExports } from 'src/design/organisms/useTableExports';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';

interface IChangesOverviewTableProps {
  changes: ChangeOverviewDto[];
  loading?: boolean;
}

export const ChangesOverviewTable: React.FC<IChangesOverviewTableProps> = ({
  changes,
  loading = true,
}) => {
  const navigate = useNavigate();

  const handleViewChange = useCallback(
    (row: ChangeOverviewDto) => {
      navigate(`/jobs/${row.jobId}/changes/view/${row.id}`);
    },
    [navigate],
  );

  const columns = useMemo<MRT_ColumnDef<ChangeOverviewDto>[]>(
    () => [
      {
        id: 'job',
        accessorFn: (row) => `${row.jobNumber ?? ''} ${row.jobName ?? ''}`,
        header: 'Job',
        size: 220,
        Cell: ({ row }) => (
          <Stack gap={0}>
            <Text c="black" fw={500}>
              {row.original.jobNumber ?? '--'}
            </Text>
            <Text c="black" size="sm">
              {row.original.jobName ?? '--'}
            </Text>
          </Stack>
        ),
      },
      {
        accessorKey: 'changeNumber',
        header: 'Change',
        size: 130,
      },
      workflowStatusColumn<ChangeOverviewDto>({ size: 150 }),
      ...changeAmountColumns<ChangeOverviewDto>(),
      {
        accessorKey: 'createdByName',
        header: 'Created By',
        size: 150,
      },
      {
        accessorKey: 'assigneeName',
        header: 'Assignee',
        size: 150,
      },
    ],
    [],
  );

  const tableState = useTableState({
    identifier: 'changes-overview-table',
    columns: columns,
    defaultSorting: [{ id: 'job', desc: false }],
  });

  const toRow = (item: ChangeOverviewDto) => ({
    ['Job Number']: item.jobNumber ?? '',
    ['Job Name']: item.jobName ?? '',
    ['Change']: item.changeNumber ?? '',
    ['Status']: item.status ?? '',
    ...changeAmountExportRow(item),
    ['Created By']: item.createdByName ?? '',
    ['Assignee']: item.assigneeName ?? '',
  });

  const tableExports = useTableExports<ChangeOverviewDto>('changes', (data) =>
    data.map(toRow),
  );

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 100 } },
      renderRowActions: ({ row }) => (
        <Group>
          <ViewActionIcon
            onClick={() => handleViewChange(row.original)}
            label="View Change"
          />
        </Group>
      ),
      enableClickToCopy: false,
      manualFiltering: false,
      manualSorting: false,
      manualPagination: false,
      columns: columns,
      data: changes,
    },
    getRowId: (row) => row.id,
    totalCount: changes.length,
    isLoading: loading,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  return table;
};
